import {
  GlobalFunction,
  registerGlobalFunction,
  unregisterGlobalFunction,
} from "pages/[orgId]/global-functions";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { UseQueryResult } from "react-query";
import { useLocalStorage } from "usehooks-ts";
import { AppTab, useApplicationContext } from "./useApplication";

export const enum ToasterMessageType {
  ERROR = "Unexpected Error",
  SUCCESS = "Success",
  UNDO = "Undo",
}

export type ToasterMessage = {
  type: ToasterMessageType;
  key: string;
  message: string;
  undoType?: "restore-tab";
  undoData?: AppTab;
  first_recorded_at: string;
  last_recorded_at: string;
  seen_count: number;
  status: "new" | "dismissed";
  // Debugging fields:
  debugStack?: string;
  debugType?: string;
  debugRaw?: string;
  debugLocation?: string;
};

export function useToasterContext() {
  return useContext(ToasterContext);
}

export function useQueryToasterErrors(query: UseQueryResult) {
  const error = useMemo(() => {
    if (!query?.error) return null;
    // Handle different error types
    if (typeof query.error === "string") return query.error;
    if (query.error instanceof Error) return query.error.message;
    return JSON.stringify(query.error);
  }, [query?.error]);

  const { addMessage } = useToasterContext();

  useEffect(() => {
    if (error && process.env.NODE_ENV === "development") {
      console.warn("API error", error);
      // Log error type and keys
      console.log("Error type:", typeof error, error);
      if (error && typeof error === "object") {
        console.log("Error keys:", Object.keys(error));
      }
      // If error is an Error object, log its stack
      if ((error as any) instanceof Error && (error as any).stack) {
        console.log("Original error stack:", (error as any).stack);
      }
      // Add stack trace for debugging
      console.trace("Toaster error stack trace");
      addMessage(ToasterMessageType.ERROR, {
        message: error || "An unexpected error occurred",
      });
    }
  }, [addMessage, error]);
}

export function useToaster() {
  const app = useApplicationContext();
  // const [messages, setMessages] = useState<ToasterMessage[]>([]);
  const [messagesData, setMessagesData] = useLocalStorage(
    "toaster-messages",
    "[]"
  );
  const messages = useMemo(() => {
    try {
      return JSON.parse(`${messagesData}`) as ToasterMessage[];
    } catch {
      return [] as ToasterMessage[];
    }
  }, [messagesData]);

  const setMessages = useCallback(
    (callback: (messages: ToasterMessage[]) => ToasterMessage[]) => {
      setMessagesData((messagesData) => {
        const messages = callback(JSON.parse(messagesData));
        return JSON.stringify(messages);
      });
    },
    [setMessagesData]
  );

  const addMessage = useCallback(
    (
      type: ToasterMessageType,
      data: Pick<ToasterMessage, "message" | "undoType" | "undoData">
    ) => {
      // Implicitly capture debug info
      const debugStack = new Error("Toaster debug stack trace").stack;
      const debugType = typeof data.message;
      let debugRaw: string | undefined = undefined;
      try {
        debugRaw = JSON.stringify(data);
      } catch {}
      const debugLocation = (debugStack || "").split("\n")[2]?.trim(); // 2nd line is the caller

      const key = messageKey({ type, message: data.message });
      setMessages((messages) => {
        const trackable = messages.find((message) => message.key === key);
        const message = trackable
          ? trackMessage(trackable)
          : createMessage({
              type,
              message: data.message,
              undoData: data.undoData,
              undoType: data.undoType,
              debugStack,
              debugType,
              debugRaw,
              debugLocation,
            });
        return trackable
          ? [message, ...messages.filter((message) => message.key !== key)]
          : [message, ...messages];
      });
    },
    [setMessages]
  );
  const dismissMessage = useCallback(
    ({ key }: Pick<ToasterMessage, "key">) => {
      setMessages((messages) =>
        messages.map((message) =>
          message.key === key ? closeMessage(message) : message
        )
      );
    },
    [setMessages]
  );

  const context = useMemo(
    () => ({ messages, addMessage, dismissMessage }),
    [messages, addMessage, dismissMessage]
  );
  useEffect(() => {
    registerGlobalFunction(GlobalFunction.getToasterContext, () => context);
    // In development or for superadmins, log debug info for each message
    // if (false) {
    //   messages.forEach((msg) => {
    //     if (
    //       msg.type === ToasterMessageType.ERROR &&
    //       (msg.debugStack || msg.debugType || msg.debugRaw || msg.debugLocation)
    //     ) {
    //       console.groupCollapsed(`[Toaster Debug] ${msg.type}: ${msg.message}`);
    //       if (msg.debugRaw) console.log("Raw:", msg.debugRaw);
    //       console.groupEnd();
    //     }
    //   });
    // }
    return () => unregisterGlobalFunction(GlobalFunction.getExploreContext);
  }, [context, messages, app]);

  return context;
}

export const ToasterContext = createContext<ReturnType<typeof useToaster>>({
  messages: [],
  addMessage() {},
  dismissMessage() {},
});

function messageKey({
  type,
  message,
}: Pick<ToasterMessage, "type" | "message">) {
  const seed =
    type === ToasterMessageType.ERROR
      ? ""
      : Math.floor(Math.random() * 1000).toString(16);
  return `${type}-${message}-${seed}`;
}

function createMessage(
  message: Pick<
    ToasterMessage,
    | "type"
    | "message"
    | "undoType"
    | "undoData"
    | "debugStack"
    | "debugType"
    | "debugRaw"
    | "debugLocation"
  >
): ToasterMessage {
  return {
    ...message,
    key: messageKey(message),
    first_recorded_at: new Date().toISOString(),
    last_recorded_at: new Date().toISOString(),
    seen_count: 1,
    status: "new",
  };
}
function trackMessage(message: ToasterMessage): ToasterMessage {
  const last_recorded_at = new Date();
  // keep closed for 2.5 seconds at least before reopening automatically
  const first_recorded_at = new Date(message.first_recorded_at);
  const reopen =
    last_recorded_at.getTime() - first_recorded_at.getTime() > 2500;
  return {
    ...message,
    status: reopen ? "new" : message.status,
    last_recorded_at: last_recorded_at.toISOString(),
    seen_count: message.seen_count + 1,
  };
}
function closeMessage(message: ToasterMessage): ToasterMessage {
  return {
    ...message,
    status: "dismissed",
  };
}
